<template>
  <div class="game-status-panel" :class="{ mobile: isMobile }">
    <ArenaStatusIndicator v-if="!isMobile && showArenas" />
    <div class="status-text" :class="{ animating }">
      <div v-if="!fight.result">
        {{ displayText }}
      </div>
      <div v-else class="fight-result">
        F{{ fight.fightNumber }}&nbsp;<span :class="fight.result" class="player">{{
          $t($capitalize(fight.result))
        }}</span
        >&nbsp;<span v-if="fight.result !== 'draw'"> {{ $t('Win') }}! </span>
      </div>
    </div>
    <div v-if="config.enableJackpot" class="jackpot-btn-wrapper">
      <div v-if="config.displayLuckyDrawPrizes" class="jackpot-physical-btn" @click="$emit('jackpotPhysicalPopup')">
        <img src="@/assets/images/jackpot-physical-btn.webp" />
      </div>
      <div class="jackpot-btn" :class="{ active }" @click="$emit('jackpotCollectPopup')">
        <img
          :class="{ active }"
          :src="`${active ? `${require('@/assets/images/fire-ic.gif')}` : `${require('@/assets/images/crown.png')}`}`"
        />
        <div class="text">
          <div class="label" :class="{ showAmount: showAmount }">
            {{ $t('Jackpot') }}
          </div>
          <Transition @after-enter="onAfterEnter">
            <div class="jackpot-pool-amount" v-show="showAmount" :class="{ showAmount: showAmount }">
              {{ currencySymbol }}
              <animated-number :value="amountAfterKM" :formatValue="formatAmount" :duration="500" />{{ amountKMSymbol }}
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ArenaStatusIndicator from '@/components/bet-panel/arena-status-indicator.vue';
import AnimatedNumber from 'animated-number-vue';
import { mapGetters } from 'vuex';
import { formatAmount } from '@/utils/helpers';
import { isMobile } from 'mobile-device-detect';

export default {
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    /*
     * The desktop split view puts the arena rail in its own band above the
     * betting area (the design has them separated), so it renders the rail
     * itself and asks this component to leave it out. Mobile keeps both
     * together, which is why the default is true.
     */
    showArenas: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AnimatedNumber,
    ArenaStatusIndicator,
  },
  data: () => {
    return {
      animating: false,
      interval: null,
      showAmount: false,
      amount: 0,
      unit: 0,
      isMobile,
    };
  },
  computed: {
    ...mapGetters('fight', ['fight']),
    ...mapGetters('general', ['config', 'member']),
    ...mapGetters('jackpot', ['currentLuckyDrawAmountDetail']),
    currencySymbol() {
      return this.member.currencySymbol;
    },
    amountKMSymbol() {
      if (this.amount > 1e6) {
        return 'M';
      } else if (this.amount > 1e3) {
        return 'K';
      }
      return '';
    },
    amountAfterKM() {
      if (this.amount > 1e6) {
        return this.amount / 1e6;
      } else if (this.amount > 1e3) {
        return this.amount / 1e3;
      }
      return this.amount;
    },
    displayText() {
      if (!this.fight.fightNumber) return this.$t('No fight at the moment');
      return this.$t('F{fightNumber} is {fightStatus}', {
        fightNumber: this.fight.fightNumber,
        fightStatus: this.$t(this.$capitalize(this.fight.status !== 'confirmed' ? this.fight.status : 'open', true)),
      });
    },
  },
  methods: {
    formatAmount,
    onAfterEnter() {
      this.amount = this.amount + this.unit;
    },
    updateJackpotAmount() {
      this.amount = this.currentLuckyDrawAmountDetail.amount;
      this.unit = this.currentLuckyDrawAmountDetail.unit;
      if (this.amount) {
        this.interval = setInterval(() => {
          this.showAmount = !this.showAmount;
        }, 5000);
      }
    },
  },
  watch: {
    'fight.status': {
      handler(newVal, oldVal) {
        this.animating = true;
        setTimeout(() => {
          this.animating = false;
        }, 5000);
      },
      immediate: true,
    },
    'currentLuckyDrawAmountDetail.amount': {
      handler(newVal, oldVal) {
        if (newVal > 0) {
          clearInterval(this.interval);
          this.updateJackpotAmount();
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
};
</script>
<style lang="scss" scoped>
.game-status-panel {
  width: 100%;
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  padding: 8px 16px;
  box-sizing: border-box;
  min-width: 468px;
  position: relative;
  background-image: linear-gradient(
    to right,
    rgba(91, 134, 175, 0.5),
    rgba(91, 134, 175, 0.5) 43%,
    rgba(108, 110, 115, 0) 96%
  );

  @include user-select-none();
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .status-text {
    font-size: 12px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: center;
    color: #fff;
    text-transform: uppercase;

    .fight-result {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;

      .player {
        color: #2196f3;
        font-size: 14px;
      }

      .banker {
        color: #c12828;
        font-size: 14px;
      }
      .draw {
        color: #4caf50;
        font-size: 14px;
      }
    }
  }

  .animating {
    animation: blinking 0.4s linear infinite;
  }

  @keyframes blinking {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes yellowBlinking {
    0% {
      box-shadow: 0 0 5px #ffee00;
    }
    50% {
      box-shadow: 0 0 20px #ffee00;
    }
    100% {
      box-shadow: 0 0 5px #ffee00;
    }
  }
  .jackpot-btn-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;

    .jackpot-physical-btn {
      height: 32px;
      cursor: pointer;

      img {
        height: 100%;
      }
    }

    .jackpot-btn {
      height: 32px;
      width: 150px;
      margin: 0 11px;
      padding: 0 1px;
      background-image: linear-gradient(to right, #ffeebc, #7b6832);
      border-radius: 16px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      cursor: pointer;
      position: relative;
      &.active {
        -webkit-animation: yellowBlinking 2s infinite;
        -moz-animation: yellowBlinking 2s infinite;
        -o-animation: yellowBlinking 2s infinite;
        animation: yellowBlinking 2s infinite;
      }

      img {
        height: 24px;
        width: 24px;
        // margin-right: 8px;
        position: absolute;
        left: 15px;

        &.active {
          height: 30px;
          width: 30px;
        }
      }

      .text {
        margin-left: 45px;
        text-transform: uppercase;
        
        font-size: 12px;
        font-weight: 800;
        text-align: center;
        color: #602b04;
        width: 90px;
        white-space: nowrap;
        overflow-x: hidden;
        display: flex;
        flex-direction: row;

        .label {
          font-variant-numeric: tabular-nums;
          width: 90px;
          min-width: 90px;
          display: inline-block;
          transition: all 0.3s ease;
          &.showAmount {
            transform: translateX(-90px);
            transition: all 0.3s ease;
          }
        }
        .jackpot-pool-amount {
          font-variant-numeric: tabular-nums;
          width: 90px;
          min-width: 90px;
          text-align: center;
          display: inline-block;
          transition: all 0.3s ease;
          font-feature-settings: 'tnum' on, 'lnum' on;
          &.showAmount {
            transform: translateX(-90px);
            transition: all 0.3s ease;
          }
        }
      }
    }
  }

  @at-root .mobile #{&} {
    margin-bottom: 0;
  }
  @at-root .vertical #{&} {
    min-width: 300px;
  }
}
</style>
